/**
 * Email send / forward / archive — typed wrappers for the
 * ``/email-management/send|forward|archive`` BE endpoints (May 2026).
 *
 * BE source of truth:
 *   - ``ando-agent/src/api/v1/endpoints/email_send.py``
 *     (router mounted at ``/email-management``).
 *
 * The provider literal is the same neutral string used in
 * ``inbox.ts::InboxProviderId`` (``"gmail" | "outlook"``). When the FE
 * surfaces these mutations from the Conversazioni drawer, the
 * ``InboxItem.provider_id`` field can be passed through directly.
 */

import { apiClient } from "../ApiClient.js";

/** Provider literal — neutral, no vendor name leaks into URLs. */
export type EmailSendProvider = "gmail" | "outlook";

// ---------------------------------------------------------------------------
// Send
// ---------------------------------------------------------------------------

/**
 * One attachment in an outgoing email. Exactly ONE of ``content_b64``
 * and ``minio_key`` must be present — the BE rejects the request
 * otherwise. Prefer ``content_b64`` for small files (≤25 MB raw bytes)
 * to skip the upload round-trip; use ``minio_key`` (returned by
 * ``uploadEmailAttachment``) for larger payloads.
 */
export interface EmailAttachmentInput {
  readonly filename: string;
  readonly mime_type?: string;
  readonly content_b64?: string;
  readonly minio_key?: string;
}

export interface SendEmailInput {
  readonly provider: EmailSendProvider;
  readonly to: ReadonlyArray<string>;
  readonly cc?: ReadonlyArray<string>;
  readonly bcc?: ReadonlyArray<string>;
  readonly subject: string;
  readonly body_text: string;
  readonly body_html?: string;
  /** When set, the BE preserves threading via ``In-Reply-To`` + ``References``. */
  readonly in_reply_to_message_id?: string;
  /** Override the sending mailbox; defaults to the user's primary connection. */
  readonly email_address?: string;
  /** Optional file attachments — inline base64 OR uploaded MinIO ref. */
  readonly attachments?: ReadonlyArray<EmailAttachmentInput>;
  /**
   * ISO-8601 UTC instant — defer the send until this moment via Temporal.
   * Must be between 30 seconds and 23 hours in the future. When present,
   * the response is the ``EmailScheduledResponse`` envelope instead of
   * ``EmailSendResponse`` — use ``isScheduledResponse`` to discriminate.
   */
  readonly scheduled_for?: string;
}

export interface EmailSendResponse {
  readonly success: boolean;
  readonly message_id: string;
  readonly provider: EmailSendProvider;
  readonly thread_id?: string | null;
}

/**
 * Returned by ``/send`` and ``/forward`` when ``scheduled_for`` is set.
 * The BE persists the staged send inside Temporal (``start_delay``);
 * pass ``scheduled_id`` to ``cancelScheduledEmail`` to abort before
 * fire time.
 */
export interface EmailScheduledResponse {
  readonly scheduled: true;
  readonly scheduled_id: string;
  readonly scheduled_for: string;
  readonly provider: EmailSendProvider;
}

/** Discriminate at the call site without leaking the ``scheduled`` literal. */
export function isScheduledEmailResponse(
  resp: EmailSendResponse | EmailScheduledResponse,
): resp is EmailScheduledResponse {
  return (resp as EmailScheduledResponse).scheduled === true;
}

export async function sendEmail(
  input: SendEmailInput,
): Promise<EmailSendResponse | EmailScheduledResponse> {
  if (!input.to || input.to.length === 0) {
    throw new Error("sendEmail: at least one recipient is required");
  }
  if (!input.subject?.trim()) throw new Error("sendEmail: subject required");
  if (!input.body_text?.trim()) throw new Error("sendEmail: body_text required");
  return await apiClient.post<EmailSendResponse | EmailScheduledResponse>(
    "/email-management/send",
    input,
  );
}

/**
 * Cancel a pending scheduled send / forward. Idempotent — the BE
 * resolves "already-cancelled" / "already-fired" workflows as the
 * same success envelope so the FE doesn't have to special-case races.
 */
export interface EmailScheduledCancelResponse {
  readonly cancelled: boolean;
  readonly scheduled_id: string;
}

export async function cancelScheduledEmail(
  scheduledId: string,
): Promise<EmailScheduledCancelResponse> {
  if (!scheduledId) throw new Error("cancelScheduledEmail: scheduled_id required");
  return await apiClient.delete<EmailScheduledCancelResponse>(
    `/email-management/scheduled/${encodeURIComponent(scheduledId)}`,
  );
}

// ---------------------------------------------------------------------------
// Attachment upload (transport B)
// ---------------------------------------------------------------------------

/**
 * Raw inline ceiling — files at or below this size go via inline
 * base64 in the JSON payload (one round-trip, lowest latency); files
 * above this go via the two-step ``uploadEmailAttachment`` → minio_key
 * path. Matches ``EMAIL_ATTACHMENT_INLINE_MAX_BYTES`` on the BE.
 */
export const EMAIL_ATTACHMENT_INLINE_MAX_BYTES = 25 * 1024 * 1024;

export interface EmailAttachmentUploadResponse {
  readonly minio_key: string;
  readonly filename: string;
  readonly mime_type: string;
  readonly size_bytes: number;
}

/**
 * Stage a file in MinIO for an upcoming email send. Returns the
 * ``minio_key`` to forward in ``SendEmailInput.attachments[].minio_key``.
 *
 * Use this when the file is larger than ``EMAIL_ATTACHMENT_INLINE_MAX_BYTES``
 * — the helper ``buildEmailAttachment`` below picks the right transport
 * automatically so callers don't have to branch.
 */
/**
 * Optional progress callback fired during the upload. Receives a
 * number in ``[0, 1]`` — the fraction of bytes uploaded so far. Useful
 * to render a progress bar on the attachment chip when files take more
 * than a moment (large files on slow connections). The callback fires
 * with ``1`` on completion and never after that.
 */
export type UploadProgressHandler = (fraction: number) => void;

export interface UploadEmailAttachmentOptions {
  readonly onProgress?: UploadProgressHandler;
  readonly signal?: AbortSignal;
}

export async function uploadEmailAttachment(
  file: File,
  options?: UploadEmailAttachmentOptions,
): Promise<EmailAttachmentUploadResponse> {
  const form = new FormData();
  form.append("file", file, file.name);
  // When no progress callback is requested, fall back to the existing
  // fetch-based path — it's lighter, plays nicer with the shared
  // ApiClient timeout / device-id / auth plumbing, and keeps the
  // happy-path code identical to the rest of the upload surface.
  if (!options?.onProgress) {
    return await apiClient.request<EmailAttachmentUploadResponse>(
      "/email-management/upload-attachment",
      { method: "POST", body: form, signal: options?.signal },
    );
  }
  // Progress reporting requires ``XMLHttpRequest`` — the fetch API
  // doesn't expose upload-stream events in any browser as of 2026
  // (the streaming-upload proposal still ships behind a flag). XHR
  // also lets us cancel via ``AbortSignal`` for parity.
  const url = new URL(
    "/api/v1/email-management/upload-attachment",
    window.location.origin,
  ).toString();
  return await new Promise<EmailAttachmentUploadResponse>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", url);
    xhr.withCredentials = true;
    xhr.upload.onprogress = (event) => {
      if (!event.lengthComputable) return;
      const pct = Math.min(1, event.loaded / event.total);
      options.onProgress?.(pct);
    };
    xhr.upload.onload = () => options.onProgress?.(1);
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText) as EmailAttachmentUploadResponse);
        } catch (err) {
          reject(
            err instanceof Error
              ? err
              : new Error("Upload response was not valid JSON"),
          );
        }
        return;
      }
      reject(new Error(`Upload failed: HTTP ${xhr.status}`));
    };
    xhr.onerror = () => reject(new Error("Upload network error"));
    xhr.onabort = () => reject(new Error("Upload aborted"));
    if (options.signal) {
      if (options.signal.aborted) {
        xhr.abort();
        return;
      }
      options.signal.addEventListener("abort", () => xhr.abort(), { once: true });
    }
    xhr.send(form);
  });
}

/**
 * Encode a small file as base64 for the inline attachment transport.
 * Returns the value to put in ``EmailAttachmentInput.content_b64``.
 *
 * Implementation note: we walk the buffer in 8 KiB chunks and use
 * ``String.fromCharCode`` + ``btoa`` because the alternative
 * (``FileReader.readAsDataURL`` + slice prefix) allocates the whole
 * data-URL string twice. Performance matters when a single email
 * carries a handful of attachments near the 25 MB ceiling.
 */
async function fileToBase64(file: File): Promise<string> {
  const buffer = await file.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  const CHUNK = 8192;
  let binary = "";
  for (let i = 0; i < bytes.length; i += CHUNK) {
    const slice = bytes.subarray(i, i + CHUNK);
    binary += String.fromCharCode.apply(null, slice as unknown as number[]);
  }
  return btoa(binary);
}

/**
 * Build a ready-to-send ``EmailAttachmentInput`` from a browser ``File``.
 * Picks the inline transport when the file fits the BE inline ceiling,
 * falls back to MinIO upload otherwise — the caller can pass the
 * resulting object straight into ``SendEmailInput.attachments``.
 */
export async function buildEmailAttachment(
  file: File,
  options?: UploadEmailAttachmentOptions,
): Promise<EmailAttachmentInput> {
  const mime_type = file.type || "application/octet-stream";
  if (file.size <= EMAIL_ATTACHMENT_INLINE_MAX_BYTES) {
    // Inline path has no network upload to track; fire the progress
    // callback once at the end to keep the chip state machine simple
    // (one transition, "preparing" → "ready").
    const content_b64 = await fileToBase64(file);
    options?.onProgress?.(1);
    return { filename: file.name, mime_type, content_b64 };
  }
  const uploaded = await uploadEmailAttachment(file, options);
  return {
    filename: uploaded.filename,
    mime_type: uploaded.mime_type,
    minio_key: uploaded.minio_key,
  };
}

// ---------------------------------------------------------------------------
// Forward
// ---------------------------------------------------------------------------

export interface ForwardEmailInput {
  readonly provider: EmailSendProvider;
  readonly original_message_id: string;
  readonly to: ReadonlyArray<string>;
  readonly cc?: ReadonlyArray<string>;
  readonly body_text?: string;
  readonly email_address?: string;
  /**
   * Optional EXTRA attachments to add to the forward (the original
   * message's own attachments are preserved by the connector). Same
   * shape as ``SendEmailInput.attachments``.
   *
   * Outlook caveat: when extras are present the BE falls back to a
   * compose-as-new path because Microsoft Graph's ``/forward`` endpoint
   * doesn't expose an ``attachments`` field. The receiver still sees a
   * normally-shaped "Fwd: " email; only the Outlook-internal
   * ``messageType=forward`` flag on the sent item differs.
   */
  readonly attachments?: ReadonlyArray<EmailAttachmentInput>;
  /** Same contract as ``SendEmailInput.scheduled_for``. */
  readonly scheduled_for?: string;
}

export async function forwardEmail(
  input: ForwardEmailInput,
): Promise<EmailSendResponse | EmailScheduledResponse> {
  if (!input.original_message_id) {
    throw new Error("forwardEmail: original_message_id required");
  }
  if (!input.to || input.to.length === 0) {
    throw new Error("forwardEmail: at least one recipient is required");
  }
  return await apiClient.post<EmailSendResponse | EmailScheduledResponse>(
    "/email-management/forward",
    {
      ...input,
      body_text: input.body_text ?? "",
    },
  );
}

// ---------------------------------------------------------------------------
// Archive
// ---------------------------------------------------------------------------

export interface ArchiveEmailInput {
  readonly provider: EmailSendProvider;
  readonly message_id: string;
  readonly email_address?: string;
}

export interface EmailArchiveResponse {
  readonly success: boolean;
}

export async function archiveEmail(
  input: ArchiveEmailInput,
): Promise<EmailArchiveResponse> {
  if (!input.message_id) throw new Error("archiveEmail: message_id required");
  return await apiClient.post<EmailArchiveResponse>(
    "/email-management/archive",
    input,
  );
}
